import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { UniversalLink } from '@plone/volto/components';
import { flattenToAppURL } from '@plone/volto/helpers';
import {
  ArrowrightIcon,
  UserIcon,
} from '@plone/components/Icons';
// Import Tag and TagGroup components
import { Tag, TagGroup } from '@plone/components';

// Import custom Button component
import { ESSButton } from '../../Atoms/ESSButton';
import './TeaserCard.scss';

/**
 * Teaser Card component for displaying a summary item in listings.
 */
const TeaserCard = ({
  title,
  description,
  imageUrl,
  url,
  date,
  tags = [],
  label,
  readMoreText,
  className = '',
  isEditMode = false,
  variant = 'vertical', // NEW: variant prop
}) => {
  const intl = useIntl();
  const internalUrl = url ? flattenToAppURL(url) : null;
  // NEW: Determine horizontal mode
  const isHorizontal = variant === 'horizontal';

  return (
    <div
      className={`teaser-card${isHorizontal ? ' teaser-card--horizontal' : ''} ${className}`.trim()}
      style={{
        borderRadius: 'var(--border-radius-none)',
        background: 'var(--card-bg, #fff)',
        height: '100%',
      }}
    >
      <div className="teaser-card__media">
        {label && <div className="teaser-card__label">{label}</div>}
        {imageUrl && (
          <img
            src={flattenToAppURL(imageUrl)}
            alt={title || ''}
            loading="lazy"
          />
        )}
      </div>
      <div
        className="teaser-card__content"
        style={{ flex: '1 1 auto', display: 'flex', flexDirection: 'column' }}
      >
        {title && (
          <h3 className="teaser-card__title">
            {internalUrl ? (
              <UniversalLink href={internalUrl}>{title}</UniversalLink>
            ) : (
              title
            )}
          </h3>
        )}
        <div className="teaser-card__metadata hidden-xs block-sm">
          {date && (
            <div className="teaser-card__date">
              {date}
            </div>
          )}
        </div>
        {description && (
          <div className="teaser-card__description">{description}</div>
        )}
        {tags.length > 0 && (
          <TagGroup
            onSelectionChange={() => {}}
            selectionMode="none"
            aria-label="Item tags"
            className="teaser-card__tags"
          >
            {tags.map((tag) => (
              <Tag key={tag} itemKey={tag}>
                {tag}
              </Tag>
            ))}
          </TagGroup>
        )}
        {/* Espaciador flexible para empujar el botón abajo */}
        <div style={{ flex: 1 }} />
        {internalUrl && !isEditMode && (
          <ESSButton
            radius="none"
            variant="negative"
            className="mx-auto"
            {...(!isHorizontal && { isFullWidth: true })}
          >
            <UniversalLink
              href={internalUrl}
              className="teaser-card__readmore-link"
            >
              {readMoreText || 'Read more'}
            </UniversalLink>
            <ArrowrightIcon size="20px" className="ml-2" />
          </ESSButton>
        )}
      </div>
    </div>
  );
};

TeaserCard.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  imageUrl: PropTypes.string,
  url: PropTypes.string,
  date: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  tags: PropTypes.array,
  label: PropTypes.string,
  readMoreText: PropTypes.string,
  className: PropTypes.string,
  isEditMode: PropTypes.bool,
  variant: PropTypes.oneOf(['vertical', 'horizontal']), // NEW
};

TeaserCard.defaultProps = {
  tags: [],
  className: '',
  isEditMode: false,
  variant: 'vertical', // NEW
};

export default TeaserCard;
